import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import IntelexFrame from "../intelex/IntelexFrame";
import {
  dataList,
  displayDate,
  intelexRequest,
  reportDetailPath,
} from "../intelex/api";

const reviewLabels = {
  pending: "Pending review",
  accepted: "Accepted",
  rejected: "Rejected",
  invalidated: "PIREP invalidated",
};

function ReviewQueue({ isAdmin, queueMode }) {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("all");
  const [reportState, setReportState] = useState("open");
  const [query, setQuery] = useState("");
  const [state, setState] = useState({ loading: true, error: "" });

  useEffect(() => {
    if (!isAdmin) {
      setState({ loading: false, error: "" });
      return undefined;
    }
    let current = true;
    intelexRequest("/api/portal/reports?scope=all")
      .then((payload) => {
        if (!current) return;
        setReports(dataList(payload));
        setState({ loading: false, error: "" });
      })
      .catch((error) => {
        if (current)
          setState({
            loading: false,
            error: error.message || "The review queue could not be loaded.",
          });
      });
    return () => {
      current = false;
    };
  }, [isAdmin]);

  const filtered = useMemo(
    () =>
      reports.filter((report) => {
        const matchesQueue =
          queueMode === "responses"
            ? report.responseRequired
            : !report.responseRequired;
        const matchesAction =
          filter === "all" || (report.reviewStatus || "pending") === filter;
        const matchesReportState =
          reportState === "all" ||
          (reportState === "closed"
            ? report.status === "closed"
            : report.status !== "closed");
        const text =
          `${report.eventNumber || ""} ${report.templateTitle || ""} ${report.submittedByName || ""} ${report.submittedBy || ""} ${report.pirepId || ""}`.toLowerCase();
        return (
          matchesQueue &&
          matchesAction &&
          matchesReportState &&
          text.includes(query.trim().toLowerCase())
        );
      }),
    [filter, query, queueMode, reportState, reports],
  );
  if (!isAdmin)
    return (
      <div className="intelex-main">
        <div className="intelex-alert" role="alert">
          Administrator access is required.
        </div>
        <Link className="intelex-secondary-button" to="/interep">
          Back to reports
        </Link>
      </div>
    );
  return (
    <div className="intelex-main intelex-review-queue-page">
      <header className="intelex-page-heading">
        <div>
          <h1>
            {queueMode === "responses"
              ? "Waiting for response"
              : "Report review queue"}
          </h1>
        </div>
      </header>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      <section className="intelex-panel">
        <div className="intelex-review-filters">
          <label>
            <span>Final decision</span>
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            >
              <option value="pending">Pending review</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="invalidated">PIREP invalidated</option>
              <option value="all">All decisions</option>
            </select>
          </label>
          <label>
            <span>Report state</span>
            <select
              value={reportState}
              onChange={(event) => setReportState(event.target.value)}
            >
              <option value="all">Open and closed</option>
              <option value="open">Open reports</option>
              <option value="closed">Closed reports</option>
            </select>
          </label>
          <label>
            <span>Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Reference, pilot, form or PIREP"
            />
          </label>
        </div>
        {state.loading ? (
          <div className="intelex-loading" role="status">
            Loading review queue...
          </div>
        ) : filtered.length ? (
          <div className="intelex-review-list">
            {filtered.map((report) => (
              <Link to={reportDetailPath(report)} key={report.id}>
                <div>
                  <strong>{report.templateTitle || "Safety report"}</strong>
                  <span>{report.eventNumber || `Report #${report.id}`}</span>
                </div>
                <dl>
                  <div>
                    <dt>Pilot</dt>
                    <dd>
                      {report.submittedByName || report.submittedBy || "-"}
                      {report.submittedByName && report.submittedBy && (
                        <small>{report.submittedBy}</small>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Submitted</dt>
                    <dd>{displayDate(report.submittedAt)}</dd>
                  </div>
                  <div>
                    <dt>Linked flight</dt>
                    <dd>{report.pirepId ? `PIREP #${report.pirepId}` : "-"}</dd>
                  </div>
                  <div>
                    <dt>Final decision</dt>
                    <dd>{reviewLabels[report.reviewStatus || "pending"]}</dd>
                  </div>
                </dl>
                <span>
                  {queueMode === "responses"
                    ? "Waiting for pilot response"
                    : report.status === "closed"
                      ? "Report closed"
                      : "Report open"}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="intelex-empty">
            <strong>No reports match this view</strong>
            <p>
              {queueMode === "responses"
                ? "No reports are currently waiting for a pilot response."
                : "Change the case status, final decision, or search terms."}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

export default function IntelexReviewQueuePage({ mode = "review" }) {
  const waitingForResponse = mode === "responses";
  return (
    <IntelexFrame
      active={waitingForResponse ? "responses" : "review"}
      title={waitingForResponse ? "Waiting for response" : "Report review queue"}
    >
      {({ isAdmin }) => (
        <ReviewQueue
          isAdmin={isAdmin}
          queueMode={waitingForResponse ? "responses" : "review"}
        />
      )}
    </IntelexFrame>
  );
}
